/**
 * This or That - Game Renderer
 * Renders binary choice game cards in chat
 */
import { t } from '../utils/i18n.js';
import { Utils } from '../utils/ui.js';
import { AppState } from '../config.js';

const lang = () => document.documentElement.lang === 'he' ? 'he' : 'en';

export function renderInvite(message, isOwn, partnerName) {
    const gd = message.game_data;
    const gameName = gd.gameName?.[lang()] || gd.gameName?.en || 'This or That';
    const emoji = gd.gameEmoji || '⚡';
    const sessionId = gd.sessionId;
    const status = gd.status;

    let actionHtml = '';
    if (status === 'expired') {
        actionHtml = `<span class="text-gray-400 text-sm">${t('games.expired')}</span>`;
    } else if (status === 'declined') {
        actionHtml = `<span class="text-gray-400 text-sm">${t('games.declined')}</span>`;
    } else if (isOwn) {
        actionHtml = `<span class="text-fuchsia-400 text-sm game-waiting-dots">${t('games.inviteSent', { name: partnerName })}</span>`;
    } else {
        actionHtml = `
            <div class="flex gap-2 mt-3">
                <button class="game-accept-btn flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-all" data-session-id="${sessionId}">
                    ${t('games.accept')}
                </button>
                <button class="game-decline-btn flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium transition-all" data-session-id="${sessionId}">
                    ${t('games.decline')}
                </button>
            </div>
        `;
    }

    return `
        <div class="game-card game-invite-card" data-session-id="${sessionId}" style="animation: gameCardAppear 0.4s ease-out">
            <div class="flex items-center gap-2 mb-2">
                <span class="text-2xl">${emoji}</span>
                <span class="font-semibold text-fuchsia-600 dark:text-fuchsia-300">${Utils.escapeHtml(gameName)}</span>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-300 mb-1">
                ${isOwn
                    ? t('games.inviteSent', { name: Utils.escapeHtml(partnerName) })
                    : t('games.wantsToPlay', { name: Utils.escapeHtml(partnerName), game: Utils.escapeHtml(gameName) })}
            </p>
            ${actionHtml}
        </div>
    `;
}

export function renderGameCard(sessionId, state, currentUserId, partnerName) {
    const round = state.current_round || 1;
    const total = state.total_rounds || 5;
    const questions = state.state?.questions || [];
    const question = questions[round - 1];
    if (!question) return '';

    const l = lang();
    const optA = question.option_a?.[l] || question.option_a?.en || 'A';
    const optB = question.option_b?.[l] || question.option_b?.en || 'B';
    const emojiA = question.emoji_a || '';
    const emojiB = question.emoji_b || '';

    // Check if current user already answered
    const answers = state.state?.answers?.[round] || {};
    const myAnswer = answers[currentUserId];
    const partnerAnswer = Object.keys(answers).find(id => String(id) !== String(currentUserId));
    const partnerAnswered = partnerAnswer && answers[partnerAnswer] && answers[partnerAnswer] !== '***';
    const bothAnswered = myAnswer && partnerAnswered;

    const progress = Math.round((round / total) * 100);

    let bodyHtml = '';
    if (bothAnswered) {
        // Reveal state
        const partnerChoice = answers[partnerAnswer];
        const matched = myAnswer === partnerChoice;
        bodyHtml = renderReveal(question, myAnswer, partnerChoice, matched, partnerName, l);
    } else if (myAnswer) {
        // Waiting for partner
        bodyHtml = `
            <div class="text-center py-4">
                <p class="text-fuchsia-500 font-medium game-waiting-dots">${t('games.waitingForAnswer', { name: Utils.escapeHtml(partnerName) })}</p>
            </div>
        `;
    } else {
        // Show options
        bodyHtml = `
            <div class="grid grid-cols-2 gap-3">
                <button class="game-option game-answer-btn p-4 rounded-xl border-2 border-gray-200 hover:border-fuchsia-400 transition-all text-center" data-session-id="${sessionId}" data-choice="a">
                    <span class="text-3xl block mb-2">${emojiA}</span>
                    <span class="text-sm font-medium">${Utils.escapeHtml(optA)}</span>
                </button>
                <button class="game-option game-answer-btn p-4 rounded-xl border-2 border-gray-200 hover:border-fuchsia-400 transition-all text-center" data-session-id="${sessionId}" data-choice="b">
                    <span class="text-3xl block mb-2">${emojiB}</span>
                    <span class="text-sm font-medium">${Utils.escapeHtml(optB)}</span>
                </button>
            </div>
        `;
    }

    return `
        <div class="game-card game-active-card" data-session-id="${sessionId}" style="animation: gameCardAppear 0.4s ease-out">
            <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-2">
                    <span class="text-xl">⚡</span>
                    <span class="font-semibold text-sm text-fuchsia-600 dark:text-fuchsia-300">${t('games.thisOrThat')}</span>
                </div>
                <span class="text-xs text-gray-500">${t('games.round', { current: round, total: total })}</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-1.5 mb-4">
                <div class="bg-fuchsia-500 h-1.5 rounded-full transition-all" style="width: ${progress}%"></div>
            </div>
            ${bodyHtml}
        </div>
    `;
}

function renderReveal(question, myChoice, partnerChoice, matched, partnerName, l) {
    const optA = question.option_a?.[l] || question.option_a?.en;
    const optB = question.option_b?.[l] || question.option_b?.en;
    const emojiA = question.emoji_a || '';
    const emojiB = question.emoji_b || '';

    const myText = myChoice === 'a' ? optA : optB;
    const myEmoji = myChoice === 'a' ? emojiA : emojiB;
    const partnerText = partnerChoice === 'a' ? optA : optB;
    const partnerEmoji = partnerChoice === 'a' ? emojiA : emojiB;

    const matchClass = matched ? 'game-reveal-match' : 'game-reveal-different';
    const matchText = matched ? t('games.matched') : t('games.different');

    return `
        <div class="game-reveal ${matchClass}" style="animation: revealSlideIn 0.5s ease-out">
            <div class="flex justify-between items-center mb-2">
                <div class="text-center flex-1">
                    <p class="text-xs text-gray-500 mb-1">${t('games.youPicked')}</p>
                    <span class="text-2xl">${myEmoji}</span>
                    <p class="text-sm font-medium">${Utils.escapeHtml(myText)}</p>
                </div>
                <div class="text-center flex-1">
                    <p class="text-xs text-gray-500 mb-1">${t('games.theyPicked', { name: Utils.escapeHtml(partnerName) })}</p>
                    <span class="text-2xl">${partnerEmoji}</span>
                    <p class="text-sm font-medium">${Utils.escapeHtml(partnerText)}</p>
                </div>
            </div>
            <p class="text-center text-sm font-semibold ${matched ? 'text-green-600' : 'text-orange-500'} mt-2">${matchText}</p>
        </div>
    `;
}

export function renderResult(result, partnerName) {
    const pct = result.compatibility_percentage || 0;
    const colorClass = pct >= 80 ? 'text-green-500' : pct >= 50 ? 'text-yellow-500' : 'text-orange-500';
    const l = lang();

    const roundsHtml = (result.perRound || []).map(r => {
        const q = r.question;
        const emojiA = q?.emoji_a || '';
        const emojiB = q?.emoji_b || '';
        const playerIds = Object.keys(r.answers);
        const icons = playerIds.map(pid => r.answers[pid] === 'a' ? emojiA : emojiB);
        return `
            <div class="flex items-center gap-2 text-sm ${r.matched ? '' : 'opacity-60'}">
                <span>${icons[0]}</span>
                <span>${r.matched ? '=' : '≠'}</span>
                <span>${icons[1]}</span>
                <span class="text-xs ${r.matched ? 'text-green-500' : 'text-gray-400'}">${r.matched ? '✓' : '✗'}</span>
            </div>
        `;
    }).join('');

    return `
        <div class="game-card game-result-card" style="animation: gameCardAppear 0.4s ease-out">
            <div class="text-center mb-4">
                <p class="text-lg font-bold">🎉 ${t('games.gameComplete')}</p>
            </div>
            <div class="game-result-score ${colorClass}" style="animation: compatibilityCountUp 0.8s ease-out">
                <span class="text-4xl font-bold">${pct}%</span>
                <p class="text-sm mt-1">${t('games.compatibility', { percent: pct })}</p>
            </div>
            <div class="flex justify-center gap-4 mt-4 flex-wrap">
                ${roundsHtml}
            </div>
        </div>
    `;
}

export function onGameUpdate(sessionId, data, currentUserId) {
    // Find the game card in DOM and update it
    const card = document.querySelector(`.game-active-card[data-session-id="${sessionId}"]`);
    if (!card) return;

    // If game completed with result, the orchestrator will handle adding result card
    // For round updates, re-render in place
    if (data.status === 'active' && data.state) {
        const partnerName = window._currentChatPartnerName || '';
        const newHtml = renderGameCard(sessionId, data, currentUserId, partnerName);
        if (newHtml) {
            card.outerHTML = newHtml;
        }
    }
}
